import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, Sun, Search, Menu, X } from "lucide-react";
import { useApp, t } from "../lib/ctx";

export const CategoryChip = ({ category, size = "sm" }) => {
  if (!category) return null;
  const sz = size === "lg" ? "text-xs px-3 py-1" : "text-[10px] px-2 py-0.5";
  return (
    <span className={`inline-flex items-center font-bold uppercase tracking-wider rounded-full text-white ${sz}`}
      style={{ backgroundColor: category.color }} data-testid={`category-chip-${category.slug}`}>
      {category.name}
    </span>
  );
};

export const Badge = ({ kind }) => {
  const map = {
    BREAKING: "bg-signal text-white",
    UPDATED: "bg-amber-500 text-black",
    SPONSORED: "bg-foreground text-background",
    LIVE: "bg-signal text-white",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${map[kind] || "bg-muted text-foreground"}`}>
      {kind === "LIVE" && <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" />}
      {kind}
    </span>
  );
};

export const BreakingTicker = ({ items }) => {
  if (!items?.length) return null;
  const text = items.map(i => i.text).join("   •   ");
  return (
    <div className="bg-signal text-white overflow-hidden" data-testid="breaking-ticker">
      <div className="flex items-center h-9">
        <div className="flex-none px-3 h-9 flex items-center bg-black/20 font-display font-extrabold tracking-widest text-[11px]">
          BREAKING
        </div>
        <div className="relative flex-1 overflow-hidden whitespace-nowrap">
          <div className="animate-marquee inline-block py-2 text-sm font-medium">
            {text}&nbsp;&nbsp;&nbsp;{text}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Header = ({ ticker }) => {
  const { theme, toggleTheme, lang, setLang } = useApp();
  const nav = useNavigate();
  const [mobileNav, setMobileNav] = React.useState(false);
  const primaryNav = [
    { label: "Politics", slug: "politics" },
    { label: "Business", slug: "business" },
    { label: "Sports", slug: "sports" },
    { label: "Entertainment", slug: "entertainment" },
  ];
  const moreNav = [
    { label: "Bangladesh", slug: "bangladesh" },
    { label: "World", slug: "world" },
    { label: "Tech", slug: "tech" },
  ];
  return (
    <>
      <BreakingTicker items={ticker} />
      <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-1.5" data-testid="header-logo">
            <span className="font-display font-extrabold text-2xl sm:text-3xl text-foreground leading-none">Newsgram</span>
            <span className="w-2 h-2 rounded-full bg-signal mt-2" />
          </Link>
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {primaryNav.map(n => (
              <Link key={n.slug} to={`/category/${n.slug}`} data-testid={`nav-${n.slug}`}
                className="px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-muted">{n.label}</Link>
            ))}
            <div className="relative group">
              <button className="px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-muted" data-testid="nav-more">More</button>
              <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition">
                <div className="bg-card border border-border rounded-xl shadow-xl p-2 min-w-[180px]">
                  {moreNav.map(n => (
                    <Link key={n.slug} to={`/category/${n.slug}`} className="block px-3 py-2 rounded-lg hover:bg-muted text-sm font-medium">{n.label}</Link>
                  ))}
                  <Link to="/explainers" className="block px-3 py-2 rounded-lg hover:bg-muted text-sm font-medium">Explainers</Link>
                  <Link to="/videos" className="block px-3 py-2 rounded-lg hover:bg-muted text-sm font-medium">Videos</Link>
                  <Link to="/live-blogs" className="block px-3 py-2 rounded-lg hover:bg-muted text-sm font-medium">Live Blogs</Link>
                  <Link to="/quiz" className="block px-3 py-2 rounded-lg hover:bg-muted text-sm font-medium">Weekly Quiz</Link>
                </div>
              </div>
            </div>
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => nav("/search")} aria-label="Search" data-testid="header-search-btn"
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted"><Search className="w-4 h-4" /></button>
            <button onClick={() => setLang(lang === "en" ? "bn" : "en")} data-testid="lang-toggle"
              className="h-9 px-3 rounded-full text-xs font-bold hover:bg-muted border border-border">
              {lang === "en" ? "বাংলা" : "EN"}
            </button>
            <button onClick={toggleTheme} aria-label="Theme" data-testid="theme-toggle"
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted"
              onClick={() => setMobileNav(v => !v)} data-testid="mobile-nav-toggle">
              {mobileNav ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {mobileNav && (
          <div className="md:hidden border-t border-border bg-background p-3 grid grid-cols-2 gap-2" data-testid="mobile-nav">
            {[...primaryNav, ...moreNav].map(n => (
              <Link key={n.slug} to={`/category/${n.slug}`} onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg hover:bg-muted text-sm font-semibold">{n.label}</Link>
            ))}
            <Link to="/explainers" onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg hover:bg-muted text-sm font-semibold">Explainers</Link>
            <Link to="/videos" onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg hover:bg-muted text-sm font-semibold">Videos</Link>
            <Link to="/quiz" onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg hover:bg-muted text-sm font-semibold">Weekly Quiz</Link>
            <Link to="/newsletter" onClick={() => setMobileNav(false)} className="px-3 py-2 rounded-lg hover:bg-muted text-sm font-semibold">Newsletter</Link>
          </div>
        )}
      </header>
    </>
  );
};

export const BottomNav = () => {
  const nav = useNavigate();
  const items = [
    { label: "Home", icon: "🏠", path: "/", testId: "bn-home" },
    { label: "Categories", icon: "🗂️", path: "/categories", testId: "bn-categories" },
    { label: "60s", icon: "⚡", path: "/sixty-seconds", testId: "bn-sixty" },
    { label: "Search", icon: "🔍", path: "/search", testId: "bn-search" },
    { label: "Saved", icon: "🔖", path: "/saved", testId: "bn-saved" },
  ];
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border h-16 grid grid-cols-5" data-testid="bottom-nav">
      {items.map(it => (
        <button key={it.path} onClick={() => nav(it.path)} data-testid={it.testId}
          className="flex flex-col items-center justify-center text-[10px] font-semibold text-muted-foreground hover:text-foreground">
          <span className="text-xl leading-none">{it.icon}</span>
          <span className="mt-0.5">{it.label}</span>
        </button>
      ))}
    </nav>
  );
};

export const Footer = () => (
  <footer className="mt-16 border-t border-border bg-card">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
      <div className="col-span-2">
        <div className="flex items-center gap-1.5"><span className="font-display font-extrabold text-2xl">Newsgram</span><span className="w-2 h-2 rounded-full bg-signal mt-2" /></div>
        <p className="text-muted-foreground mt-2 max-w-sm">The news, minus the noise. Short, scannable news for Bangladesh.</p>
      </div>
      <div>
        <div className="font-bold mb-2">Company</div>
        <ul className="space-y-1 text-muted-foreground">
          <li><Link to="/about" className="hover:text-foreground">About</Link></li>
          <li><Link to="/about#team" className="hover:text-foreground">Team</Link></li>
          <li><Link to="/about#standards" className="hover:text-foreground">Editorial standards</Link></li>
          <li><Link to="/about#corrections" className="hover:text-foreground">Corrections</Link></li>
          <li><Link to="/advertise" className="hover:text-foreground">Advertise</Link></li>
          <li><Link to="/tip" className="hover:text-foreground">Submit a tip</Link></li>
        </ul>
      </div>
      <div>
        <div className="font-bold mb-2">Legal</div>
        <ul className="space-y-1 text-muted-foreground">
          <li><Link to="/about#privacy" className="hover:text-foreground">Privacy policy</Link></li>
          <li><Link to="/about#terms" className="hover:text-foreground">Terms</Link></li>
          <li><Link to="/about#fact-check" className="hover:text-foreground">Fact-check</Link></li>
          <li><Link to="/admin" className="hover:text-foreground">Admin</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-border py-4 text-xs text-muted-foreground text-center">© {new Date().getFullYear()} Newsgram. Sample content for demo.</div>
  </footer>
);
